import { Table, TableCard } from "@light-technology/components";
import { useEffect, useRef, useState } from "react";
import { formatCents, formatCurrency } from "../../utilities/currency";
import type { UsageKwh } from "../PlanEditPage/common";
import type { PlanValues } from "./common";
import { PlanComparisonRow } from "./PlanComparisonRow";

type PlanComparisonDataTableProps = {
  hasSolarBuyback?: boolean;
  planNames: string[];
  values: PlanValues[];
  unavailableString?: string;
  usageKwh?: UsageKwh;
  showAnnualSavings?: boolean;
  highlightCommission?: boolean;
  hideUniformTermLength?: boolean;
  economicsOnly?: boolean;
  hideEvRows?: boolean;
  hideCommission?: boolean;
  hideRenewableContent?: boolean;
};

export const PlanComparisonDataTable = ({
  hasSolarBuyback,
  planNames,
  values,
  unavailableString,
  usageKwh = 1000,
  showAnnualSavings = false,
  highlightCommission = false,
  hideUniformTermLength = false,
  economicsOnly = false,
  hideEvRows = false,
  hideCommission = false,
  hideRenewableContent = false,
}: PlanComparisonDataTableProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const scrollEl = wrapper.querySelector<HTMLElement>(".overflow-x-auto");
    if (!scrollEl) return;

    const update = () => {
      setCanScrollLeft(scrollEl.scrollLeft > 0);
      setCanScrollRight(
        scrollEl.scrollLeft < scrollEl.scrollWidth - scrollEl.clientWidth - 1,
      );
    };

    update();
    scrollEl.addEventListener("scroll", update);
    const observer = new ResizeObserver(update);
    observer.observe(scrollEl);
    return () => {
      scrollEl.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, []);

  const colWidth = `${(100 / (planNames.length + 1)).toFixed(2)}%`;

  const termLengths = values.map((v) => v.termMonths);
  const allSameTermLength =
    termLengths.length > 0 &&
    termLengths.every((t) => t !== undefined && t === termLengths[0]);

  const hasEvPlans = values.some(
    (v) =>
      v.evChargingFee !== undefined ||
      v.evChargingEnergyRateKwh !== undefined ||
      v.evChargingSchedule !== undefined ||
      v.evChargingHours !== undefined ||
      v.evChargingIncludesTdsp !== undefined ||
      v.evChargingAssumedChargingKwh !== undefined ||
      v.evChargingLimit !== undefined ||
      v.evChargingAdditionalInfo !== undefined,
  );

  const avgCostField: keyof PlanValues =
    usageKwh === 500
      ? "average500Kwh"
      : usageKwh === 1000
        ? "average1000Kwh"
        : "average2000Kwh";
  const annualCost = values.map((v) => {
    const rate = v[avgCostField];
    if (rate === undefined) return undefined;
    return rate * usageKwh * 12;
  });
  const lowestRate = values.reduce<number | undefined>((min, v) => {
    const rate = v[avgCostField];
    if (rate === undefined) return min;
    return min === undefined || rate < min ? rate : min;
  }, undefined);
  const annualSavings = values.map((v) => {
    const rate = v[avgCostField];
    if (lowestRate === undefined || rate === undefined) return undefined;
    return (rate - lowestRate) * usageKwh * 12;
  });

  const annualSavingsTooltip =
    "Estimated additional annual cost compared to the lowest-priced plan, based on the selected usage level.";
  const displayRateTooltip =
    "The average cost per kWh is what most customers are interested in to predict their monthly costs.";
  const deliveryFees = deliveryFeesDescription(values);
  const costPerKwhTooltip = deliveryFees
    ? `${displayRateTooltip} ${deliveryFees}`
    : displayRateTooltip;
  const energyRateTooltip =
    "Energy rates change based on market conditions, but lock in for the full contract term once the customer enrolls.";

  const baseFeeRow = !economicsOnly && (
    <PlanComparisonRow
      fieldName="Monthly base fee"
      values={values}
      field="baseChargeMonth"
      display={(baseFee: number) => `$${baseFee.toFixed(2)}`}
    />
  );

  return (
    <div
      ref={wrapperRef}
      style={{ "--col-w": colWidth } as React.CSSProperties}
    >
      <TableCard.Root className="relative bg-tertiary">
        {canScrollLeft && (
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-[var(--color-bg-tertiary)] to-transparent" />
        )}
        {canScrollRight && (
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-[var(--color-bg-tertiary)] to-transparent" />
        )}
        <Table aria-label="Plan comparison">
          <Table.Header>
            <Table.Head
              isRowHeader
              id="field_name"
              label=""
              className="w-[var(--col-w)] min-w-28"
            />
            {planNames.map((planName) => (
              <Table.Head
                key={planName}
                id={planName}
                className="w-[var(--col-w)] min-w-28 justify-center [&_div]:justify-center"
              >
                <div className="flex justify-center">
                  <span className="text-xs font-medium text-primary">
                    {planName}
                  </span>
                </div>
              </Table.Head>
            ))}
          </Table.Header>
          <Table.Body>
            {!economicsOnly &&
              !(hideUniformTermLength && allSameTermLength) && (
                <PlanComparisonRow
                  fieldName="Term length"
                  values={values}
                  field="termMonths"
                  display={(termMonths: number) =>
                    `${termMonths.toString()} months`
                  }
                  findMax
                  unavailableString={unavailableString}
                />
              )}
            {!economicsOnly && (
              <PlanComparisonRow
                fieldName="Energy rate per kWh"
                values={values}
                field="energyRateKwh"
                display={(energyRate: number) =>
                  `${(energyRate * 100).toFixed(1)}¢`
                }
                tooltip={energyRateTooltip}
              />
            )}
            {!economicsOnly && hasSolarBuyback && (
              <PlanComparisonRow
                fieldName="Buyback rate per kWh"
                values={values}
                field="exportRateKwh"
                display={(buybackRate: number) => {
                  if (!buybackRate) {
                    return "";
                  }
                  return `${(buybackRate * 100).toFixed(1)}¢`;
                }}
                findMax
              />
            )}
            {baseFeeRow}
            {!economicsOnly && hasEvPlans && !hideEvRows && (
              <PlanComparisonRow
                fieldName="EV charging fee"
                values={values}
                field="evChargingFee"
                display={(evFee: number) => `$${evFee.toFixed(2)}`}
              />
            )}
            {!economicsOnly && hasEvPlans && !hideEvRows && (
              <PlanComparisonRow
                fieldName="EV charging schedule"
                values={values}
                field="evChargingHours"
                display={(hours: number) => `${hours.toString()} hours`}
                valueTooltip={(_hours, plan) => evChargingScheduleTooltip(plan)}
                findMax
              />
            )}
            {!economicsOnly && hasEvPlans && !hideEvRows && (
              <PlanComparisonRow
                fieldName="EV charging covers delivery fees"
                values={values}
                field="evChargingIncludesTdsp"
                display={(includesTdsp: boolean) =>
                  includesTdsp ? "Yes" : "No"
                }
              />
            )}
            {!economicsOnly && !hideCommission && (
              <PlanComparisonRow
                fieldName="Monthly commission"
                values={values}
                field="monthlyCommission"
                display={(commission: number) => `$${commission.toFixed(2)}`}
                noHighlight={!highlightCommission}
                findMax={highlightCommission}
              />
            )}
            {!economicsOnly && !hideRenewableContent && (
              <PlanComparisonRow
                fieldName="Renewable content"
                values={values}
                field="renewableContentPercent"
                display={(renewablePercent: number) =>
                  `${renewablePercent.toString()}%`
                }
                findMax
              />
            )}
            {/* TODO: Add early termination fee once editable */}
            {/* <PlanComparisonRow
              fieldName="Early termination fee"
              values={values}
              index={5}
              display={(earlyTermination: number) =>
                `$${earlyTermination.toFixed(2)}`
              }
            /> */}
            <PlanComparisonRow
              fieldName={`Cost per kWh (${usageKwh.toLocaleString()} kWh/mo usage)`}
              values={values}
              field={avgCostField}
              display={(energyRate: number) =>
                `${(energyRate * 100).toFixed(1)}¢`
              }
              tooltip={costPerKwhTooltip}
              valueTooltip={(_energyRate, plan) =>
                plan?.evChargingAssumedChargingKwh !== undefined
                  ? `Assumes ${plan.evChargingAssumedChargingKwh.toString()} kWh/mo of EV charging`
                  : undefined
              }
            />
            {showAnnualSavings && (
              <>
                <PlanComparisonRow
                  fieldName={`Est. annual cost`}
                  computedValues={annualCost}
                  display={(value) => {
                    if (value === 0) return "-";
                    const dollars = Math.round(Math.abs(value));
                    const formatted = `$${dollars.toLocaleString()}`;
                    return value > 0 ? formatted : `-${formatted}`;
                  }}
                  tooltip={annualSavingsTooltip}
                />
                <PlanComparisonRow
                  fieldName="Cost comparison"
                  computedValues={annualSavings}
                  display={(value) => {
                    if (value === 0) return "Lowest";
                    const dollars = Math.round(Math.abs(value));
                    const formatted = `$${dollars.toLocaleString()}`;
                    return value > 0 ? `+${formatted}` : `-${formatted}`;
                  }}
                  tooltip={annualSavingsTooltip}
                />
              </>
            )}
          </Table.Body>
        </Table>
      </TableCard.Root>
    </div>
  );
};

const deliveryFeesDescription = (values: PlanValues[]) => {
  const deliveryChargeKwh = values.find(
    (v) => v.deliveryChargeKwh !== undefined,
  )?.deliveryChargeKwh;
  const deliveryBaseMonth = values.find(
    (v) => v.deliveryBaseMonth !== undefined,
  )?.deliveryBaseMonth;
  if (deliveryChargeKwh === undefined || deliveryBaseMonth === undefined) {
    return undefined;
  }
  return `Includes utility delivery charges of ${formatCents(deliveryChargeKwh * 100)}¢ per kWh and ${formatCurrency(deliveryBaseMonth)}/mo.`;
};

const evChargingScheduleTooltip = (plan?: PlanValues) => {
  const parts: string[] = [];
  if (plan?.evChargingEnergyRateKwh !== undefined) {
    parts.push(
      `EV charging available for ${formatCents(plan.evChargingEnergyRateKwh * 100)}¢ per kWh`,
    );
  }
  if (plan?.evChargingSchedule !== undefined) {
    // TODO: format the schedule once the BE returns structured start/end
    // times instead of a free-form string (see PR #563 review).
    parts.push(plan.evChargingSchedule);
  }
  if (parts.length === 0) return undefined;
  return parts.map((part, i) => (
    <>
      {i > 0 && (
        <>
          :<br />
        </>
      )}
      {part}
    </>
  ));
};
